import LoadingBox from '../../components/LoadingBox';
import React, { useReducer, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, LayoutTemplate, Pencil, Plus, Trash2, X } from 'lucide-react';
import { paths } from '../../routes/paths';
import { useTranslation } from 'react-i18next';
import rifaService from '../../services/rifa.service';
import casillaService from '../../services/casilla.service';
import settingsService from '../../services/settings.service';
import ConfirmModal from '../../components/ConfirmModal';
import BoardRemapModal from '../../components/BoardRemapModal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';
import LotteryAcknowledgment from '../../components/LotteryAcknowledgment/LotteryAcknowledgment';
import { BoardModeFields, SorteosPickerFields } from '../../components/rifa/RifaModeFields';
import {
  computeBoardTotalTickets,
  mostrarNumerosOf,
  pickerModeOf,
  sharedBoardSize,
  sorteosEnabledOf,
  toSharedGridView,
  toTicketsPerSquare,
} from '../../lib/boardPlans';

const emptyCategory = {
  nombre: '',
  color: '#FFD700',
  filas: 4,
  columnas: 7,
  precioCasilla: 100,
  ticketsPorCasilla: 1,
};

const emptySorteo = { titulo: '', fecha: '', objetivoVenta: 0, premios: [] };
const emptyPremio = { nombre: '', descripcion: '', puesto: 1 };

function buildBoundsMap(normalizedCategories) {
  const m = new Map();
  normalizedCategories.forEach(cat => {
    const n = String(cat.nombre || '').trim();
    if (!n) return;
    const filas = Math.max(1, Number(cat.filas) || 1);
    const columnas = Math.max(1, Number(cat.columnas) || 1);
    const prev = m.get(n) || { filas: 0, columnas: 0 };
    m.set(n, {
      filas: Math.max(prev.filas, filas),
      columnas: Math.max(prev.columnas, columnas),
    });
  });
  return m;
}

/** Shared board: reserved/sold squares that fall outside the new single grid. */
function computeSharedOobOccupied(casillas, sharedBoard) {
  const { filas, columnas } = sharedBoardSize({ sharedBoard });
  return casillas.filter(
    c =>
      c.estado !== 'Disponible' &&
      ((Number(c.fila) || 0) >= filas || (Number(c.columna) || 0) >= columnas)
  );
}

/** Total raffle numbers for a board config (merged duplicate category names like the server). */
function computeBoardScopeFromCategories(normalizedCategories) {
  const m = new Map();
  for (const cat of normalizedCategories) {
    const nombre = String(cat.nombre || '').trim();
    if (!nombre) continue;
    const filas = Math.max(1, Number(cat.filas) || 1);
    const columnas = Math.max(1, Number(cat.columnas) || 1);
    const ticketsPorCasilla = toTicketsPerSquare(cat.ticketsPorCasilla);
    const prev = m.get(nombre);
    if (!prev) {
      m.set(nombre, { filas, columnas, ticketsPorCasilla });
    } else {
      m.set(nombre, {
        filas: Math.max(prev.filas, filas),
        columnas: Math.max(prev.columnas, columnas),
        ticketsPorCasilla: Math.max(prev.ticketsPorCasilla, ticketsPorCasilla),
      });
    }
  }
  return [...m.values()].reduce(
    (sum, cat) => sum + cat.filas * cat.columnas * cat.ticketsPorCasilla,
    0
  );
}

function computeOobOccupied(casillas, normalizedCategories) {
  const boundsMap = buildBoundsMap(normalizedCategories);
  return casillas.filter(c => {
    if (c.estado === 'Disponible') return false;
    const b = boundsMap.get(String(c.categoria || '').trim());
    if (!b) return true;
    const f = Number(c.fila) || 0;
    const col = Number(c.columna) || 0;
    return f >= b.filas || col >= b.columnas;
  });
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

function EditRifa() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const { id } = params;
  const [{ loading, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    loadingUpdate: false,
  });

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState('standard');
  const [ticketFocus, setTicketFocus] = useState('sorteos');
  const [totalTickets, setTotalTickets] = useState(0);
  const [precioTicket, setPrecioTicket] = useState(200);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState({ ...emptyCategory });
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [ticketImage, setTicketImage] = useState();
  const [rifaImage, setRifaImage] = useState();
  const [imagenTableroVendido, setImagenTableroVendido] = useState();
  const [sorteosEnabled, setSorteosEnabled] = useState(true);
  const [pickerMode, setPickerMode] = useState('both');
  const [mostrarNumeros, setMostrarNumeros] = useState(true);
  const [boardMode, setBoardMode] = useState('separate');
  const [sharedBoard, setSharedBoard] = useState({ filas: 4, columnas: 7 });
  const isSharedBoard = tipo === 'board' && boardMode === 'shared';
  const [sorteos, setSorteos] = useState([]);
  const [currentSorteo, setCurrentSorteo] = useState({ ...emptySorteo });
  const [currentPremio, setCurrentPremio] = useState({ ...emptyPremio });

  const [showModal, setShowModal] = useState(false);
  const [showRemapModal, setShowRemapModal] = useState(false);
  const [oobForRemap, setOobForRemap] = useState([]);
  const [boardCasillas, setBoardCasillas] = useState([]);
  const [savedTicketScope, setSavedTicketScope] = useState({ board: 0, standard: 0 });
  const [showScopeIncreaseModal, setShowScopeIncreaseModal] = useState(false);
  const [scopeIncreaseDetails, setScopeIncreaseDetails] = useState({ previous: 0, next: 0 });
  const [scopePendingRemap, setScopePendingRemap] = useState(null);
  const handleClose = () => setShowModal(false);

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (tipo === 'board' && categories.length === 0) {
      toast.error(t('raffle.boardNeedsCategories') || 'Add at least one category to the board');
      return;
    }
    setShowModal(true);
  };

  const [editingSorteoIndex, setEditingSorteoIndex] = useState(null);
  const [editingPremioIndex, setEditingPremioIndex] = useState(null);
  const [showPremioForm, setShowPremioForm] = useState(false);
  const [showSorteoForm, setShowSorteoForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const rifaData = await rifaService.getRifa(id);
        setNombre(rifaData.nombre);
        setDescripcion(rifaData.descripcion);
        setTipo(rifaData.tipo || 'standard');
        setTicketFocus(rifaData.ticketFocus || 'sorteos');
        setTotalTickets(rifaData.totalTickets);
        setPrecioTicket(rifaData.precioTicket || 0);
        setTicketImage(rifaData.ticketImage);
        setRifaImage(rifaData.rifaImage);
        setImagenTableroVendido(rifaData.imagenTableroVendido);
        setSorteos(rifaData.sorteos || []);
        setCategories(rifaData.config?.categories || []);
        setSorteosEnabled(sorteosEnabledOf(rifaData));
        setPickerMode(pickerModeOf(rifaData));
        setMostrarNumeros(mostrarNumerosOf(rifaData));
        setBoardMode(rifaData.config?.boardMode === 'shared' ? 'shared' : 'separate');
        if (rifaData.config?.boardMode === 'shared') {
          setSharedBoard(sharedBoardSize(rifaData.config));
        }
        setSavedTicketScope({
          board: (rifaData.tipo || 'standard') === 'board' ? rifaData.totalTickets || 0 : 0,
          standard: rifaData.tipo === 'standard' ? rifaData.totalTickets || 0 : 0,
        });
        if ((rifaData.tipo || 'standard') === 'board') {
          try {
            const cas = await casillaService.getCasillasByRifa(id);
            setBoardCasillas(cas);
          } catch {
            setBoardCasillas([]);
          }
        } else {
          setBoardCasillas([]);
        }
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [id]);

  const computedTotalTickets = computeBoardTotalTickets({ boardMode, sharedBoard, categories });
  // Switching board layout regenerates every square: only allowed with no reservations/sales
  const boardModeLocked = boardCasillas.some(c => c.estado !== 'Disponible');
  const sharedRemapView = isSharedBoard
    ? toSharedGridView(
        { config: { sharedBoard } },
        boardCasillas,
        t('raffle.sharedBoardLabel', 'Tablero')
      )
    : null;

  const normalizeCategory = cat => ({
    nombre: cat.nombre || '',
    color: cat.color || '#FFD700',
    filas: Math.max(1, Number(cat.filas) || 1),
    columnas: Math.max(1, Number(cat.columnas) || 1),
    precioCasilla: Math.max(1, Number(cat.precioCasilla) || 1),
    ticketsPorCasilla: toTicketsPerSquare(cat.ticketsPorCasilla),
  });

  const handleEditCategory = index => {
    setCurrentCategory(normalizeCategory(categories[index]));
    setEditingCategoryIndex(index);
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryIndex(null);
    setCurrentCategory({ ...emptyCategory });
  };

  const handleSaveCategory = () => {
    if (isSharedBoard && !String(currentCategory.nombre || '').trim()) {
      toast.error(t('raffle.planRequiredFields', 'El nombre del plan es obligatorio'));
      return;
    }
    if (!currentCategory.nombre || currentCategory.filas < 1 || currentCategory.columnas < 1) {
      toast.error(
        t('raffle.categoryRequiredFields') || 'Category name, rows and columns are required'
      );
      return;
    }
    const payload = normalizeCategory(currentCategory);
    if (editingCategoryIndex !== null) {
      const next = [...categories];
      next[editingCategoryIndex] = payload;
      setCategories(next);
      setEditingCategoryIndex(null);
    } else {
      setCategories([...categories, payload]);
    }
    setCurrentCategory({ ...emptyCategory });
  };

  const handleRemoveCategory = index => {
    setCategories(categories.filter((_, i) => i !== index));
    if (editingCategoryIndex === index) {
      setEditingCategoryIndex(null);
      setCurrentCategory({ ...emptyCategory });
    } else if (editingCategoryIndex !== null && index < editingCategoryIndex) {
      setEditingCategoryIndex(editingCategoryIndex - 1);
    }
  };

  const editRifaHandler = async (remapFromModal = null, opts = {}) => {
    const { acknowledgeScopeIncrease = false } = opts;
    try {
      const normalizedCats = categories.map(normalizeCategory);
      const bounds = buildBoundsMap(normalizedCats);

      // Shared board: the category on a sold square is a snapshot of the chosen plan, so
      // removing/renaming plans is fine; only the grid size matters.
      if (tipo === 'board' && !isSharedBoard) {
        for (const c of boardCasillas) {
          if (c.estado === 'Disponible') continue;
          if (!bounds.has(String(c.categoria || '').trim())) {
            toast.error(
              t('raffle.boardRemapCategoryRemoved') ||
                'A category with reservations was removed or renamed. Restore it or cancel.'
            );
            return;
          }
        }
      }

      if (tipo === 'board' && !remapFromModal) {
        const oob = isSharedBoard
          ? computeSharedOobOccupied(boardCasillas, sharedBoard)
          : computeOobOccupied(boardCasillas, normalizedCats);
        if (oob.length > 0) {
          setOobForRemap(oob);
          setShowRemapModal(true);
          return;
        }
      }

      let newTicketScope;
      let previousTicketScope;
      if (tipo === 'board') {
        newTicketScope = isSharedBoard
          ? computeBoardTotalTickets({ boardMode, sharedBoard, categories: normalizedCats })
          : computeBoardScopeFromCategories(normalizedCats);
        previousTicketScope = savedTicketScope.board;
      } else {
        newTicketScope = Math.max(0, parseInt(totalTickets, 10) || 0);
        previousTicketScope = savedTicketScope.standard;
      }

      if (!acknowledgeScopeIncrease && newTicketScope > previousTicketScope) {
        setScopePendingRemap(remapFromModal ?? null);
        setScopeIncreaseDetails({ previous: previousTicketScope, next: newTicketScope });
        setShowScopeIncreaseModal(true);
        if (Array.isArray(remapFromModal) && remapFromModal.length > 0) {
          setShowRemapModal(false);
          setOobForRemap([]);
        }
        return;
      }

      setShowScopeIncreaseModal(false);
      setScopePendingRemap(null);

      dispatch({ type: 'UPDATE_REQUEST' });
      const payload = {
        nombre,
        descripcion,
        tipo,
        ticketFocus,
        ticketImage,
        rifaImage,
        imagenTableroVendido,
        totalTickets: tipo === 'board' ? 0 : totalTickets,
        precioTicket: tipo === 'board' ? 0 : precioTicket,
        sorteos,
        sorteosEnabled,
        // Hidden numbers force the random picker (the server normalizes it too)
        pickerMode: mostrarNumeros ? pickerMode : 'random',
        mostrarNumeros,
        config:
          tipo === 'board'
            ? {
                boardMode,
                categories: normalizedCats,
                ...(isSharedBoard ? { sharedBoard } : {}),
              }
            : {},
      };
      if (Array.isArray(remapFromModal) && remapFromModal.length > 0) {
        payload.boardCasillaRemap = remapFromModal;
      }
      await rifaService.updateRifa(id, payload);
      dispatch({ type: 'UPDATE_SUCCESS' });
      toast.success(t('raffle.raffleUpdated'));
      navigate('/AdminScreen/rifas');
    } catch (err) {
      dispatch({ type: 'UPDATE_FAIL' });
      if (err.response?.status === 409 && err.response?.data?.oobOccupied?.length) {
        setOobForRemap(err.response.data.oobOccupied);
        setShowRemapModal(true);
        toast.info(t('raffle.boardRemapRequired') || 'Place each reservation on the new board.');
        return;
      }
      toast.error(getError(err));
    }
  };

  const uploadPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      let r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (ticketImage) {
        formData.append('documentId', ticketImage._id);
      }
      formData.append('type', 'ticketImage');

      const response = await settingsService.uploadSettingsImg(formData);
      setTicketImage(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const uploadRifaPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      let r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (rifaImage) {
        formData.append('documentId', rifaImage._id);
      }
      formData.append('type', 'rifaImage');

      const response = await settingsService.uploadSettingsImg(formData);
      setRifaImage(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const uploadBoardSoldPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      const r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (imagenTableroVendido) formData.append('documentId', imagenTableroVendido._id);
      formData.append('type', 'imagenTableroVendido');
      const response = await settingsService.uploadSettingsImg(formData);
      setImagenTableroVendido(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const addSorteo = () => {
    if (!currentSorteo.titulo || !currentSorteo.fecha) {
      toast.error(t('raffle.sorteoRequiredFields'));
      return;
    }
    setSorteos([...sorteos, { ...currentSorteo, premios: [...currentSorteo.premios] }]);
    setCurrentSorteo({ ...emptySorteo });
    setShowSorteoForm(false);
  };

  const removeSorteo = index => {
    setSorteos(sorteos.filter((_, i) => i !== index));
  };

  const addPremio = () => {
    if (!currentPremio.nombre || !currentPremio.descripcion) {
      toast.error(t('raffle.premioRequiredFields'));
      return;
    }

    // Calculate the next position number based on existing premios
    const nextPuesto =
      currentSorteo.premios.length > 0
        ? Math.max(...currentSorteo.premios.map(p => p.puesto)) + 1
        : 1;

    setCurrentSorteo({
      ...currentSorteo,
      premios: [...currentSorteo.premios, { ...currentPremio, puesto: currentPremio.puesto }],
    });

    // Reset the form with the next position number
    const followingPuesto = nextPuesto + 1;
    setCurrentPremio({ ...emptyPremio, puesto: followingPuesto });
    setShowPremioForm(false);
  };

  const removePremio = index => {
    setCurrentSorteo({
      ...currentSorteo,
      premios: currentSorteo.premios.filter((_, i) => i !== index),
    });
  };

  const clearCurrentSorteo = () => {
    setCurrentSorteo({ ...emptySorteo });
    setCurrentPremio({ ...emptyPremio });
  };

  const startEditingSorteo = (sorteo, index) => {
    setEditingSorteoIndex(index);
    setCurrentSorteo({
      _id: sorteo._id,
      titulo: sorteo.titulo,
      fecha: new Date(sorteo.fecha).toISOString().slice(0, 16),
      objetivoVenta: sorteo.objetivoVenta || 0,
      premios: sorteo.premios || [],
    });
    setShowSorteoForm(true);
  };

  const updateSorteo = () => {
    if (!currentSorteo.titulo || !currentSorteo.fecha) {
      toast.error(t('raffle.sorteoRequiredFields'));
      return;
    }
    const updatedSorteos = [...sorteos];
    updatedSorteos[editingSorteoIndex] = { ...currentSorteo, premios: [...currentSorteo.premios] };
    setSorteos(updatedSorteos);
    setEditingSorteoIndex(null);
    setCurrentSorteo({ ...emptySorteo });
    setShowSorteoForm(false);
  };

  const cancelEditing = () => {
    setEditingSorteoIndex(null);
    setCurrentSorteo({ ...emptySorteo });
    setCurrentPremio({ ...emptyPremio });
    setShowSorteoForm(false);
  };

  const startEditingPremio = (premio, index) => {
    setEditingPremioIndex(index);
    setCurrentPremio({
      _id: premio._id,
      nombre: premio.nombre,
      descripcion: premio.descripcion,
      puesto: premio.puesto,
    });
    setShowPremioForm(true);
  };

  const updatePremio = () => {
    if (!currentPremio.nombre || !currentPremio.descripcion) {
      toast.error(t('raffle.premioRequiredFields'));
      return;
    }

    setCurrentSorteo({
      ...currentSorteo,
      premios: currentSorteo.premios.map((premio, index) =>
        index === editingPremioIndex ? { ...currentPremio } : premio
      ),
    });
    setEditingPremioIndex(null);
    setCurrentPremio({ ...emptyPremio });
    setShowPremioForm(false);
  };

  const cancelEditingPremio = () => {
    setEditingPremioIndex(null);
    setCurrentPremio({ ...emptyPremio });
  };

  const showPremioFormHandler = () => {
    const nextPuesto =
      currentSorteo.premios.length > 0
        ? Math.max(...currentSorteo.premios.map(p => p.puesto)) + 1
        : 1;

    setCurrentPremio({
      ...currentPremio,
      puesto: nextPuesto,
    });
    setShowPremioForm(true);
  };

  const showSorteoFormHandler = () => {
    setCurrentSorteo({ ...emptySorteo });
    setShowSorteoForm(true);
  };

  return (
    <div className='space-y-6'>
      {(loading || loadingUpdate) && <LoadingBox overlay />}

      <Helmet>
        <title>{t('raffle.editRaffle')}</title>
      </Helmet>

      <ConfirmModal
        show={showModal}
        title={t('common.saveChanges')}
        message={t('common.confirmSaveChanges')}
        onConfirm={() => {
          handleClose();
          editRifaHandler();
        }}
        onCancel={handleClose}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />
      <BoardRemapModal
        show={showRemapModal}
        isSaving={loadingUpdate}
        onHide={() => {
          setShowRemapModal(false);
          setOobForRemap([]);
        }}
        categories={
          sharedRemapView
            ? sharedRemapView.categories.map(c => ({ ...c, nombre: c.label }))
            : categories.map(normalizeCategory)
        }
        casillas={
          sharedRemapView
            ? sharedRemapView.casillas.map(c => ({
                ...c,
                categoria: sharedRemapView.categories[0].label,
              }))
            : boardCasillas
        }
        oobCasillas={
          sharedRemapView
            ? oobForRemap.map(c => ({ ...c, categoria: sharedRemapView.categories[0].label }))
            : oobForRemap
        }
        onApply={async remap => {
          await editRifaHandler(remap);
        }}
      />
      <ConfirmModal
        show={showScopeIncreaseModal}
        title={t('raffle.scopeIncreaseTitle')}
        message={
          tipo === 'board'
            ? t('raffle.scopeIncreaseBodyBoard', {
                previous: scopeIncreaseDetails.previous,
                next: scopeIncreaseDetails.next,
              })
            : t('raffle.scopeIncreaseBodyStandard', {
                previous: scopeIncreaseDetails.previous,
                next: scopeIncreaseDetails.next,
              })
        }
        onConfirm={() => {
          editRifaHandler(scopePendingRemap, { acknowledgeScopeIncrease: true });
        }}
        onCancel={() => {
          setShowScopeIncreaseModal(false);
          setScopePendingRemap(null);
        }}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />

      <PageHeader
        title={t('raffle.editRaffle')}
        actions={
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              <ArrowLeft className='h-4 w-4' />
              {t('common.back')}
            </Button>
            <Button type='submit' form='rifaMainForm'>
              {t('common.saveChanges')}
            </Button>
          </div>
        }
      />

      <form id='rifaMainForm' onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('raffle.editRaffle')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* Raffle type */}
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                <strong>{t('raffle.type') || 'Voucher Type'}</strong>
              </label>
              <select
                className='h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                value={tipo}
                onChange={e => setTipo(e.target.value)}
              >
                <option value='standard'>
                  {t('raffle.typeStandard') || 'Standard (numbered tickets)'}
                </option>
                <option value='board'>{t('raffle.typeBoard') || 'Board (Colaboradores)'}</option>
              </select>
            </div>

            <SorteosPickerFields
              t={t}
              sorteosEnabled={sorteosEnabled}
              onSorteosChange={setSorteosEnabled}
              pickerMode={pickerMode}
              onPickerChange={setPickerMode}
              mostrarNumeros={mostrarNumeros}
              onMostrarNumerosChange={setMostrarNumeros}
            />

            {/* Ticket PDF focus */}
            <div className='space-y-1.5'>
              {sorteosEnabled && (
                <>
                  <label className='text-sm font-medium text-foreground'>
                    <strong>{t('raffle.ticketFocus')}</strong>
                  </label>
                  <select
                    className='h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={ticketFocus}
                    onChange={e => setTicketFocus(e.target.value)}
                  >
                    <option value='sorteos'>{t('raffle.ticketFocusSorteos')}</option>
                    <option value='premios'>{t('raffle.ticketFocusPremios')}</option>
                  </select>
                  <p className='text-xs text-muted-foreground'>{t('raffle.ticketFocusHint')}</p>
                </>
              )}
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => navigate(paths.admin.rifaTicketDesign(id))}
              >
                <LayoutTemplate className='h-4 w-4' />
                {t('ticketDesign.link')}
              </Button>
            </div>

            {/* Name + Total Tickets (standard only, disabled) */}
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>{t('common.name')}</label>
                <input
                  type='text'
                  required
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                />
              </div>

              {tipo === 'standard' && (
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.ticketQuantity')}
                  </label>
                  <input
                    type='number'
                    required
                    min='1'
                    disabled
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed'
                    value={totalTickets}
                  />
                  <p className='text-xs text-muted-foreground'>
                    {t('raffle.totalTicketsDisabled')}
                  </p>
                </div>
              )}
            </div>

            {/* Rifa image */}
            <div className='grid gap-4 sm:grid-cols-2 items-end'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.uploadRaffleImage')}
                </label>
                <input
                  type='file'
                  className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                  onChange={uploadRifaPicture}
                  name='rifaImage'
                />
              </div>
              <div>
                <img
                  alt={t('raffle.raffleImage')}
                  src={rifaImage?.fileLink || '/images/emptyPhoto.png'}
                  style={{ maxWidth: '100px', maxHeight: '100px' }}
                />
              </div>
            </div>

            {/* Ticket price (standard only) */}
            {tipo === 'standard' && (
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.ticketPrice')}
                </label>
                <input
                  type='number'
                  required
                  min='1'
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={precioTicket}
                  onChange={e => setPrecioTicket(parseInt(e.target.value, 10) || 0)}
                />
              </div>
            )}

            {/* Lottery acknowledgment (standard only) */}
            {tipo === 'standard' && <LotteryAcknowledgment precioTicket={precioTicket} />}

            {/* Ticket image */}
            <div className='grid gap-4 sm:grid-cols-2 items-end'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.uploadTicketImage')}
                </label>
                <input
                  type='file'
                  className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                  onChange={uploadPicture}
                  name='ticketImage'
                />
              </div>
              <div>
                <img
                  alt={t('raffle.ticketImage')}
                  src={ticketImage?.fileLink || '/images/emptyPhoto.png'}
                  style={{ maxWidth: '100px', maxHeight: '100px' }}
                />
              </div>
            </div>

            {/* Description */}
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                {t('common.description')}
              </label>
              <input
                type='text'
                required
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Board categories — outside main form (same as NuevaRifa) */}
      {tipo === 'board' && (
        <div className='space-y-4'>
          <BoardModeFields
            t={t}
            boardMode={boardMode}
            onModeChange={setBoardMode}
            sharedBoard={sharedBoard}
            onSharedBoardChange={setSharedBoard}
            modeLocked={boardModeLocked}
          />
          <h4 className='text-base font-semibold text-foreground mt-4 mb-2'>
            {isSharedBoard
              ? t('raffle.plans', 'Planes')
              : t('raffle.boardCategories') || 'Board Categories'}
            {categories.length > 0 && (
              <span className='text-sm text-muted-foreground ml-2'>
                ({computedTotalTickets} {t('raffle.totalTickets') || 'total tickets'})
              </span>
            )}
          </h4>

          {/* Board sold square image */}
          <div className='grid gap-4 sm:grid-cols-2 items-end'>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                {t('raffle.boardSoldSquareImage')}
              </label>
              <input
                type='file'
                accept='image/*'
                className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                onChange={uploadBoardSoldPicture}
                name='imagenTableroVendido'
              />
              <p className='text-xs text-muted-foreground'>
                {t('raffle.boardSoldSquareImageHint')}
              </p>
            </div>
            <div>
              <img
                alt=''
                src={imagenTableroVendido?.fileLink || '/images/emptyPhoto.png'}
                style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'cover' }}
              />
            </div>
          </div>

          {/* Category list */}
          <div className='space-y-2'>
            {categories.map((cat, index) => (
              <div
                key={index}
                className={`flex items-center justify-between rounded-lg border p-3 ${
                  editingCategoryIndex === index ? 'border-ring border-2' : 'border-border'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      backgroundColor: cat.color,
                      display: 'inline-block',
                      border: '1px solid #ccc',
                    }}
                  />
                  <div>
                    <strong>{cat.nombre}</strong>
                    {!isSharedBoard && (
                      <span className='text-muted-foreground ml-2 text-sm'>
                        {cat.filas}×{cat.columnas} = {cat.filas * cat.columnas}{' '}
                        {t('raffle.squares') || 'squares'}
                      </span>
                    )}
                    <Badge variant='secondary' className='ml-2'>
                      ${cat.precioCasilla}/sq
                    </Badge>
                    <Badge variant='info' className='ml-1'>
                      {cat.ticketsPorCasilla} ticket{cat.ticketsPorCasilla !== 1 ? 's' : ''}/sq
                    </Badge>
                  </div>
                </div>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='icon-sm'
                    title={t('common.edit')}
                    onClick={() => handleEditCategory(index)}
                  >
                    <Pencil className='h-3.5 w-3.5' />
                  </Button>
                  <Button
                    variant='destructive'
                    size='icon-sm'
                    onClick={() => handleRemoveCategory(index)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Category builder */}
          <Card>
            <CardHeader>
              <CardTitle>
                {editingCategoryIndex !== null
                  ? isSharedBoard
                    ? t('raffle.updatePlan', 'Actualizar plan')
                    : t('raffle.updateCategory')
                  : isSharedBoard
                    ? t('raffle.addPlan', 'Agregar plan')
                    : t('raffle.addCategory')}
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid gap-4 sm:grid-cols-4'>
                <div className='space-y-1.5 sm:col-span-2'>
                  <label className='text-sm font-medium text-foreground'>{t('common.name')}</label>
                  <input
                    type='text'
                    placeholder='Oro'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.nombre}
                    onChange={e =>
                      setCurrentCategory({ ...currentCategory, nombre: e.target.value })
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('common.color') || 'Color'}
                  </label>
                  <input
                    type='color'
                    className='h-9 w-16 rounded-md border border-input cursor-pointer'
                    value={currentCategory.color}
                    onChange={e =>
                      setCurrentCategory({ ...currentCategory, color: e.target.value })
                    }
                  />
                </div>
                {!isSharedBoard && (
                  <div className='space-y-1.5'>
                    <label className='text-sm font-medium text-foreground'>
                      {t('raffle.rows') || 'Rows'}
                    </label>
                    <input
                      type='number'
                      min='1'
                      className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                      value={currentCategory.filas}
                      onChange={e =>
                        setCurrentCategory({
                          ...currentCategory,
                          filas: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                )}
              </div>
              <div className='grid gap-4 sm:grid-cols-3'>
                {!isSharedBoard && (
                  <div className='space-y-1.5'>
                    <label className='text-sm font-medium text-foreground'>
                      {t('raffle.columns') || 'Columns'}
                    </label>
                    <input
                      type='number'
                      min='1'
                      className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                      value={currentCategory.columnas}
                      onChange={e =>
                        setCurrentCategory({
                          ...currentCategory,
                          columnas: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                )}
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.pricePerSquare') || 'Price per Square'}
                  </label>
                  <input
                    type='number'
                    min='1'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.precioCasilla}
                    onChange={e =>
                      setCurrentCategory({
                        ...currentCategory,
                        precioCasilla: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.ticketsPerSquare') || 'Tickets per Square'}
                  </label>
                  <input
                    type='number'
                    min='0'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.ticketsPorCasilla}
                    onChange={e =>
                      setCurrentCategory({
                        ...currentCategory,
                        ticketsPorCasilla: Math.max(0, parseInt(e.target.value) || 0),
                      })
                    }
                  />
                </div>
              </div>
              <div className='flex gap-2 flex-wrap'>
                <Button variant='secondary' onClick={handleSaveCategory}>
                  {editingCategoryIndex !== null ? (
                    <>
                      <Pencil className='h-4 w-4' />
                      {isSharedBoard
                        ? t('raffle.updatePlan', 'Actualizar plan')
                        : t('raffle.updateCategory')}
                    </>
                  ) : (
                    <>
                      <Plus className='h-4 w-4' />
                      {isSharedBoard
                        ? t('raffle.addPlan', 'Agregar plan')
                        : t('raffle.addCategory')}
                    </>
                  )}
                </Button>
                {editingCategoryIndex !== null && (
                  <Button variant='outline' onClick={cancelCategoryEdit}>
                    <X className='h-4 w-4' />
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── SORTEOS ─────────────────────────────────────────── */}
      {sorteosEnabled && (
        <div className='space-y-4'>
          <h4 className='text-base font-semibold text-foreground mt-4 mb-2'>
            {t('raffle.sorteos')}
          </h4>

          {/* List of added sorteos */}
          <div className='space-y-2'>
            {sorteos.map((sorteo, index) => (
              <div
                key={index}
                className='flex items-center justify-between rounded-lg border border-border p-3'
              >
                <div>
                  <strong>{sorteo.titulo}</strong>{' '}
                  <span className='text-muted-foreground'>
                    - {new Date(sorteo.fecha).toLocaleDateString()}
                  </span>
                  <br />
                  <small className='text-muted-foreground'>
                    {sorteo.premios.length} {t('raffle.prizes')}
                  </small>
                  <div>
                    <small className='text-muted-foreground'>
                      {t('raffle.salesTarget')}: {sorteo.objetivoVenta}
                    </small>
                  </div>
                  {/* Display premios in a nested list */}
                  <div className='mt-2 space-y-1'>
                    {sorteo.premios.map((premio, premioIndex) => (
                      <div
                        key={premioIndex}
                        className='rounded border border-border px-2 py-1 text-xs text-muted-foreground'
                      >
                        {premio.puesto}º - {premio.nombre}: {premio.descripcion}
                      </div>
                    ))}
                  </div>
                </div>
                <div className='flex gap-2 shrink-0 ml-3'>
                  <Button
                    variant='default'
                    size='sm'
                    onClick={() => startEditingSorteo(sorteo, index)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button variant='destructive' size='icon-sm' onClick={() => removeSorteo(index)}>
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Add new sorteo form */}
          <Card>
            <CardContent className='pt-6'>
              {!showSorteoForm ? (
                <Button variant='default' onClick={showSorteoFormHandler}>
                  <Plus className='h-4 w-4' />
                  {t('raffle.addSorteo')}
                </Button>
              ) : (
                <>
                  <h5 className='text-sm font-semibold text-foreground mb-3'>
                    {editingSorteoIndex !== null ? t('raffle.editSorteo') : t('raffle.addSorteo')}
                  </h5>
                  <div className='space-y-4'>
                    <div className='grid gap-4 sm:grid-cols-2'>
                      <div className='space-y-1.5'>
                        <label className='text-sm font-medium text-foreground'>
                          {t('common.title')}
                        </label>
                        <input
                          type='text'
                          className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                          value={currentSorteo.titulo}
                          onChange={e =>
                            setCurrentSorteo({ ...currentSorteo, titulo: e.target.value })
                          }
                        />
                      </div>
                      <div className='space-y-1.5'>
                        <label className='text-sm font-medium text-foreground'>
                          {t('common.date')}
                        </label>
                        <input
                          type='datetime-local'
                          className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                          value={currentSorteo.fecha}
                          onChange={e =>
                            setCurrentSorteo({ ...currentSorteo, fecha: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className='space-y-1.5'>
                      <label className='text-sm font-medium text-foreground'>
                        {t('raffle.salesTarget')}
                      </label>
                      <input
                        type='number'
                        className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                        value={currentSorteo.objetivoVenta}
                        onChange={e =>
                          setCurrentSorteo({
                            ...currentSorteo,
                            objetivoVenta: parseInt(e.target.value),
                          })
                        }
                      />
                    </div>

                    {/* List of premios for current sorteo */}
                    <div className='space-y-2'>
                      {currentSorteo.premios.map((premio, index) => (
                        <div
                          key={index}
                          className='flex items-center justify-between rounded-lg border border-border p-3'
                        >
                          <div>
                            <strong>
                              {premio.puesto}º - {premio.nombre}
                            </strong>
                            <br />
                            <small className='text-muted-foreground'>{premio.descripcion}</small>
                          </div>
                          <div className='flex gap-2'>
                            <Button
                              variant='default'
                              size='sm'
                              onClick={() => startEditingPremio(premio, index)}
                            >
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant='destructive'
                              size='icon-sm'
                              onClick={() => removePremio(index)}
                            >
                              <Trash2 className='h-3.5 w-3.5' />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add new premio button */}
                    {!showPremioForm && (
                      <div className='flex justify-center'>
                        <Button variant='default' onClick={showPremioFormHandler}>
                          <Plus className='h-4 w-4' />
                          {t('raffle.addPrize')}
                        </Button>
                      </div>
                    )}

                    {/* Premio form */}
                    {showPremioForm && (
                      <Card>
                        <CardHeader>
                          <CardTitle className='text-sm'>
                            {editingPremioIndex !== null
                              ? t('raffle.editPrize')
                              : t('raffle.addPrize')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-4'>
                          <div className='grid gap-4 sm:grid-cols-2'>
                            <div className='space-y-1.5'>
                              <label className='text-sm font-medium text-foreground'>
                                {t('common.name')}
                              </label>
                              <input
                                type='text'
                                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                                value={currentPremio.nombre}
                                onChange={e =>
                                  setCurrentPremio({ ...currentPremio, nombre: e.target.value })
                                }
                              />
                            </div>
                            <div className='space-y-1.5'>
                              <label className='text-sm font-medium text-foreground'>
                                {t('common.position')}
                              </label>
                              <input
                                type='number'
                                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                                value={currentPremio.puesto}
                                onChange={e =>
                                  setCurrentPremio({
                                    ...currentPremio,
                                    puesto: parseInt(e.target.value),
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className='space-y-1.5'>
                            <label className='text-sm font-medium text-foreground'>
                              {t('common.description')}
                            </label>
                            <input
                              type='text'
                              className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                              value={currentPremio.descripcion}
                              onChange={e =>
                                setCurrentPremio({ ...currentPremio, descripcion: e.target.value })
                              }
                            />
                          </div>
                        </CardContent>
                        <CardFooter className='justify-center gap-3'>
                          {editingPremioIndex !== null ? (
                            <>
                              <Button variant='default' onClick={updatePremio}>
                                {t('common.update')}
                              </Button>
                              <Button
                                variant='outline'
                                onClick={() => {
                                  cancelEditingPremio();
                                  setShowPremioForm(false);
                                }}
                              >
                                {t('common.cancel')}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant='default'
                                onClick={() => {
                                  addPremio();
                                  setShowPremioForm(false);
                                }}
                              >
                                {t('common.add')}
                              </Button>
                              <Button
                                variant='outline'
                                onClick={() => {
                                  setCurrentPremio({ ...emptyPremio });
                                  setShowPremioForm(false);
                                }}
                              >
                                {t('common.cancel')}
                              </Button>
                            </>
                          )}
                        </CardFooter>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </CardContent>
            {showSorteoForm && (
              <CardFooter className='justify-center gap-3'>
                {editingSorteoIndex !== null ? (
                  <>
                    <Button variant='default' onClick={updateSorteo}>
                      {t('common.update')}
                    </Button>
                    <Button
                      variant='outline'
                      onClick={() => {
                        cancelEditing();
                        setShowSorteoForm(false);
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant='default' onClick={addSorteo}>
                      {t('common.add')}
                    </Button>
                    <Button
                      variant='outline'
                      onClick={() => {
                        clearCurrentSorteo();
                        setShowSorteoForm(false);
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                )}
              </CardFooter>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
export default EditRifa;
